import { z } from "zod"
import { ArrowDown, ArrowUp, Equals } from "@phosphor-icons/react"

import { formatSignedNumber } from "~/utils/formatting"

// Shared breakdown pieces, here so the cards don't import each other (circular imports).

// Every panel must read its persisted toggle through this one schema.
export type BreakdownViewMode = "aggregated" | "weekly"

export const BREAKDOWN_VIEW_OPTIONS: { value: BreakdownViewMode; label: string }[] = [
  { value: "aggregated", label: "Aggregated" },
  { value: "weekly", label: "Week by week" },
]

export const breakdownViewSchema = z.enum(["aggregated", "weekly"])

export const signedUnits = (value: number): string => formatSignedNumber(value)

/** One decimal, to match the row badge beside it. */
export const signedPct = (value: number): string => `${formatSignedNumber(value * 100, 1)}%`

/**
 * A y-axis ceiling from the tallest column ANY answer can produce, so toggling an answer moves only
 * the simulated column, not the axis. `undefined` for an empty chart.
 */
export const pinnedAxisMax = (tallest: number): number | undefined => {
  if (tallest <= 0) return undefined
  const step = Math.pow(10, Math.floor(Math.log10(tallest)) - 1)
  return Math.ceil((tallest * 1.12) / step) * step
}

// Absolute quantity → share of `level` (floored by pre-orders). Shares are conserved, so a pin
// reflows when `level` changes.
export const absoluteToShare = (abs: number, level: number, floor = 0): number => {
  const clamped = Math.max(floor, Math.round(abs))
  return level > 0 ? clamped / level : 0
}

const SHARE_EPS = 1e-9

// Scales `entries` down proportionally to sum to `room`, never taking one below its floor.
const shrinkToFloors = (
  entries: [number, number][],
  room: number,
  floorOf: (ms: number) => number,
): Map<number, number> => {
  const out = new Map<number, number>()
  let free = entries
  let left = room
  for (;;) {
    const mass = free.reduce((s, [, v]) => s + v, 0)
    const scale = mass > 0 ? Math.max(0, left) / mass : 0
    const below = free.filter(([ms, v]) => v * scale < floorOf(ms) - SHARE_EPS)
    if (below.length === 0) {
      for (const [ms, v] of free) out.set(ms, v * scale)
      return out
    }
    for (const [ms] of below) {
      out.set(ms, floorOf(ms))
      left -= floorOf(ms)
    }
    free = free.filter(([ms, v]) => v * scale >= floorOf(ms) - SHARE_EPS)
  }
}

/**
 * Fits a pin write into Σ shares ≤ 1, which setDemandDatedOverrides enforces (rounding alone can
 * exceed it). The edit wins and other pins yield proportionally; only an edit over what the other
 * pins' floors leave is capped. With `floors` (share per week), yielding pins stop at theirs and
 * the pins leave room for the unpinned weeks' floors, which the engine raises those weeks to.
 */
export const fitPinShares = (
  next: Map<number, number>,
  touched: number[],
  floors: ReadonlyMap<number, number> = new Map(),
): { pins: Map<number, number>; capped: boolean; yielded: boolean } => {
  const floorOf = (ms: number) => floors.get(ms) ?? 0
  const touchedSet = new Set(touched)
  const touchedEntries = [...next].filter(([ms]) => touchedSet.has(ms))
  const otherEntries = [...next].filter(([ms]) => !touchedSet.has(ms))
  const want = touchedEntries.reduce((s, [, v]) => s + v, 0)
  const others = otherEntries.reduce((s, [, v]) => s + v, 0)
  const otherFloors = otherEntries.reduce((s, [ms]) => s + floorOf(ms), 0)
  // Unpinned weeks' floors are owed too, so the pins may only fill what they leave.
  const room = 1 - [...floors].reduce((s, [ms, f]) => (next.has(ms) ? s : s + f), 0)

  if (want + others <= room + SHARE_EPS) return { pins: next, capped: false, yielded: false }
  const capped = want > room - otherFloors + SHARE_EPS
  const pins = capped
    ? new Map([
        ...shrinkToFloors(touchedEntries, room - otherFloors, floorOf),
        ...otherEntries.map(([ms]): [number, number] => [ms, floorOf(ms)]),
      ])
    : new Map([...touchedEntries, ...shrinkToFloors(otherEntries, room - want, floorOf)])
  return { pins, capped, yielded: true }
}

export type PinFitNote =
  /**
   * The edit was cut to what the total has left: `byEdits` when weeks already set took part of it,
   * `byPreorders` when other weeks' pre-orders did.
   */
  | { kind: "capped"; units: number; byEdits: boolean; byPreorders: boolean }
  /** Every other week is set, so the edited period took the rest of the total. */
  | { kind: "filled" }

/**
 * Fits a hold-the-total edit. Weeks already set keep their units; the edit gets what it asked, up
 * to what the total has left after them and after the pre-orders of weeks nobody set, which the
 * engine raises those weeks to. Once every other week is set, the edit takes exactly what is left,
 * so the weeks still add up to the total. Pre-orders are only owed while the total covers them.
 */
export const fitPinUnits = (input: {
  /** Shares of `level` by week start. */
  pins: Map<number, number>
  /** Edited weeks → units wanted. */
  targets: Map<number, number>
  level: number
  /** Every horizon week → its pre-ordered units. */
  floors: Map<number, number>
}): { pins: Map<number, number>; note: PinFitNote | null } => {
  const { pins, targets, level, floors } = input
  if (level <= 0) return { pins: new Map(), note: null }
  let floorSum = 0
  for (const f of floors.values()) floorSum += f
  const floorsApply = floorSum <= level + 1e-9
  const floorOf = (ms: number) => (floorsApply ? (floors.get(ms) ?? 0) : 0)

  let setElsewhere = 0
  for (const [ms, share] of pins) if (!targets.has(ms)) setElsewhere += share * level
  let owedPreorders = 0
  let unsetWeeks = 0
  for (const ms of floors.keys()) {
    if (targets.has(ms) || pins.has(ms)) continue
    owedPreorders += floorOf(ms)
    unsetWeeks++
  }
  const room = Math.max(0, level - setElsewhere - owedPreorders)

  const wanted = new Map([...targets].map(([ms, u]) => [ms, Math.max(floorOf(ms), Math.round(u))]))
  let wantedSum = 0
  for (const u of wanted.values()) wantedSum += u
  let note: PinFitNote | null = null
  if (wantedSum > room + 1e-9) {
    note = {
      kind: "capped",
      units: Math.round(room),
      byEdits: setElsewhere > 0.5,
      byPreorders: owedPreorders > 0,
    }
  } else if (unsetWeeks === 0 && wantedSum < room - 1e-9) {
    note = { kind: "filled" }
  }
  // Capped or filled, the edit is exactly the room, spread over its weeks as asked.
  const scale = note && wantedSum > 0 ? room / wantedSum : 1
  const even = note && wantedSum <= 0 ? room / Math.max(1, wanted.size) : 0
  const next = new Map(pins)
  for (const [ms, u] of wanted) next.set(ms, (note ? u * scale + even : u) / level)
  // Rounding alone can still nudge Σ past 1.
  return { pins: fitPinShares(next, [...targets.keys()]).pins, note }
}

/**
 * Re-shares week pins for a total moving from `level` to `total`, so every pin kept holds its
 * units. `drop` weeks lose their pin and `set` weeks are pinned at the given units.
 */
const reshareWeekPins = (
  pins: Map<number, number>,
  level: number,
  total: number,
  change: { drop?: Iterable<number>; set?: Map<number, number> },
): Map<number, number> => {
  // Every week is zero, so no share means anything.
  if (total <= 0) return new Map()
  const drop = new Set(change.drop)
  const set = change.set ?? new Map<number, number>()
  const next = new Map<number, number>()
  for (const [ms, share] of pins) {
    if (!drop.has(ms) && !set.has(ms)) next.set(ms, (share * level) / total)
  }
  for (const [ms, units] of set) next.set(ms, units / total)
  // Rounding and pre-order floors can still overshoot the server's Σ ≤ 1.
  return fitPinShares(next, [...set.keys()]).pins
}

type WeekEditState = {
  /** Shares of `level`, keyed by week start. */
  pins: Map<number, number>
  level: number
  /** Units each week's edits moved the total by, keyed by week start. */
  edits: Map<number, number>
}

/**
 * A "Let it move" edit: each target week's difference moves the total and is booked as that week's
 * edit, and every other pin is re-shared to keep its units. A week whose edits net to zero is back
 * where it started, so it loses its pin too. `targets` are the edited weeks' new units and
 * `current` their units now.
 */
export const moveWeekEdit = (
  state: WeekEditState & { targets: Map<number, number>; current: Map<number, number> },
): { total: number; pins: Map<number, number>; edits: Map<number, number> } => {
  const edits = new Map(state.edits)
  const set = new Map(state.targets)
  const drop: number[] = []
  let delta = 0
  for (const [ms, units] of state.targets) {
    // Against the units the planner sees, so the edit is exactly what they typed over it.
    const moved = units - Math.round(state.current.get(ms) ?? 0)
    delta += moved
    const booked = (edits.get(ms) ?? 0) + moved
    if (booked !== 0) {
      edits.set(ms, booked)
      continue
    }
    edits.delete(ms)
    set.delete(ms)
    drop.push(ms)
  }
  const total = Math.max(0, Math.round(state.level + delta))
  return {
    total,
    pins: reshareWeekPins(state.pins, state.level, total, { set, drop }),
    edits,
  }
}

/**
 * Takes `weeks` back: their pins go, and the units their edits moved come off the total, so every
 * other pin keeps its units.
 */
export const takeBackWeekEdits = (
  state: WeekEditState & { weeks: number[] },
): { total: number; pins: Map<number, number>; edits: Map<number, number> } => {
  const edits = new Map(state.edits)
  let removed = 0
  for (const ms of state.weeks) {
    removed += edits.get(ms) ?? 0
    edits.delete(ms)
  }
  const total = Math.max(0, Math.round(state.level - removed))
  return {
    total,
    pins: reshareWeekPins(state.pins, state.level, total, { drop: state.weeks }),
    edits,
  }
}

/** Colour tracks whether the figure moves the plan, never whether it is editable. */
const deltaTone = (value: number, contributing: boolean): string => {
  if (!contributing) return "text-fg-disabled"
  if (value > 0) return "text-badge-green-fg-text"
  if (value < 0) return "text-badge-red-fg-text"
  return "text-fg-subtle"
}

export const DeltaCell = ({
  value,
  suffix,
  contributing = true,
}: {
  value: number
  suffix?: string
  /** False when the row is switched off: its figure is not in the plan. */
  contributing?: boolean
}) => {
  const Icon = value > 0 ? ArrowUp : value < 0 ? ArrowDown : Equals
  const tone = deltaTone(value, contributing)
  return (
    <span className={`tabular-nums flex items-center gap-0.5 paragraph-sm ${tone}`}>
      <Icon className="size-3" />
      {formatSignedNumber(value)}
      {suffix}
    </span>
  )
}
